import { FC, FormEvent, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import Button from "@/components/Button";
import { FormLayout, Input, Label, Larger, Textarea } from "@/components/Forms";
import { api } from "@/utils/api";
import { handleFormErrors } from "@/utils/helpers";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import useAlerts from "@/hooks/useAlerts";
import InternalChannelsField from "./InternalChannelsField";
import { AudiencableChannel, TestingAudienceResource } from "@/types/resources";
import ExternalChannelField from "./ExternalChannelField";
import SelectedChannelsList from "./SelectedChannelsList";

export interface FormDataT {
  name: string;
  notes: string;
  channels: AudiencableChannel[];
}

interface Props {
  onClose: () => void;
  editableAudience?: TestingAudienceResource;
}

const TestingAudienceForm: FC<Props> = ({ onClose, editableAudience }) => {
  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const ref = useRef<HTMLFormElement>(null);

  const {
    register,
    handleSubmit,
    setError,
    control,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      name: editableAudience?.name ?? "",
      notes: editableAudience?.notes ?? "",
      channels: editableAudience?.channels ?? [],
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await (editableAudience
          ? api.put<FormDataT, TestingAudienceResource>(
              `/v1/testing-audiences/${editableAudience.id}`,
              data
            )
          : api.post<FormDataT, TestingAudienceResource>(
              "/v1/testing-audiences",
              data
            ));

        queryClient.invalidateQueries(["testing_audiences"]);

        onClose();

        alerts.success(
          editableAudience
            ? "Testing audience successfully updated"
            : "New testing audience successfully created"
        );
      } catch (error) {
        handleFormErrors<FormDataT>(error, setError, alerts);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading="Testing Audiences" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off" ref={ref}>
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <Label htmlFor="nameInput">Name</Label>
                <Input
                  $hasError={!!errors.name}
                  id="nameInput"
                  type="text"
                  placeholder="Add name"
                  {...register("name")}
                />
                {!!errors.name && (
                  <ValidationMessage>{errors.name.message}</ValidationMessage>
                )}
              </Larger>
              <Larger>
                <Label htmlFor="notesTextarea">Note</Label>
                <Textarea
                  id="notesTextarea"
                  placeholder="Add note"
                  $hasError={!!errors.notes}
                  {...register("notes")}
                />
                {!!errors.notes && (
                  <ValidationMessage>{errors.notes.message}</ValidationMessage>
                )}
              </Larger>
              <InternalChannelsField<FormDataT>
                control={control}
                name="channels"
                parentRef={ref}
              />
              <Larger>
                <ExternalChannelField<FormDataT>
                  control={control}
                  name="channels"
                />
                {!!errors.channels && (
                  <ValidationMessage>
                    {errors.channels.message}
                  </ValidationMessage>
                )}
              </Larger>
            </FormLayout>
            <SelectedChannelsList control={control} />
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {editableAudience ? "Update" : "Add"}
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default TestingAudienceForm;
